// Synthesised sound effects (WebAudio, no files). Each voice has a short attack (no clicks), an optional filter and
// detuned layer (warmth), and some reverb; a per-sound rate limit and a voice cap keep busy races from piling up.
import { state } from '../data/store.js';
import { audio, masterVolume, type Bus } from './engine.js';

interface ToneOpts { type?: OscillatorType; vol?: number; to?: number; attack?: number; lp?: number; detune?: number; pan?: number; verb?: number }
interface NoiseOpts { vol?: number; filter?: BiquadFilterType; f?: number; to?: number; q?: number; attack?: number; pan?: number; verb?: number }

/** Same sound restarted sooner than this (s) is dropped: ten simultaneous hits would just be louder noise. */
export const RETRIGGER_S = 0.045;
/** Simultaneous voices beyond this are dropped. */
export const MAX_VOICES = 40;

const lastAt = new Map<string, number>();
let voices = 0;

/** Whether a sound may start now, given when it last started and how many voices play (pure, for tests). */
export const mayPlay = (now: number, last: number | undefined, active: number): boolean =>
  active < MAX_VOICES && (last === undefined || now - last >= RETRIGGER_S);

/** Voice output: optional stereo position, dry to the effects bus, wet to the reverb. */
function out(b: Bus, pan = 0, verb = 1): GainNode {
  const g = b.ac.createGain();
  let node: AudioNode = g;
  if (pan && b.ac.createStereoPanner) { const p = b.ac.createStereoPanner(); p.pan.value = pan; g.connect(p); node = p; }
  node.connect(b.sfx);
  if (verb > 0) { const s = b.ac.createGain(); s.gain.value = verb; node.connect(s).connect(b.verb); }
  return g;
}

function envelope(g: GainNode, t: number, vol: number, attack: number, dur: number) {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(vol, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + Math.max(attack + 0.01, dur));
}

function track(src: AudioScheduledSourceNode) { voices++; src.onended = () => { voices--; }; }

export function sfx(name: string) {
  if (!name || masterVolume(state.settings.volume) <= 0) return;
  const b = audio();
  if (!b) return;
  const t0 = b.ac.currentTime;
  if (!mayPlay(t0, lastAt.get(name), voices)) return;
  lastAt.set(name, t0);

  const tone = (f: number, at: number, dur: number, o: ToneOpts = {}) => {
    const t = t0 + at, layers = o.detune ? [-o.detune, o.detune] : [0];
    const g = b.ac.createGain(), dest = out(b, o.pan, o.verb ?? 1);
    let head: AudioNode = g;
    if (o.lp) { const flt = b.ac.createBiquadFilter(); flt.type = 'lowpass'; flt.frequency.value = o.lp; flt.Q.value = 0.7; g.connect(flt); head = flt; }
    head.connect(dest);
    envelope(g, t, (o.vol ?? 0.08) / Math.sqrt(layers.length), o.attack ?? 0.006, dur);
    for (const cents of layers) {
      const osc = b.ac.createOscillator();
      osc.type = o.type ?? 'triangle'; osc.detune.value = cents;
      osc.frequency.setValueAtTime(f, t);
      if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t + dur);
      osc.connect(g); osc.start(t); osc.stop(t + dur + 0.05); track(osc);
    }
  };
  const noise = (at: number, dur: number, o: NoiseOpts = {}) => {
    const t = t0 + at, src = b.ac.createBufferSource(), flt = b.ac.createBiquadFilter(), g = b.ac.createGain();
    src.buffer = b.noise; src.loop = true;
    flt.type = o.filter ?? 'lowpass'; flt.Q.value = o.q ?? 0.8;
    flt.frequency.setValueAtTime(o.f ?? 1200, t);
    if (o.to) flt.frequency.exponentialRampToValueAtTime(o.to, t + dur);
    envelope(g, t, o.vol ?? 0.1, o.attack ?? 0.004, dur);
    src.connect(flt).connect(g).connect(out(b, o.pan, o.verb ?? 0.5));
    // random offset in the shared buffer: two identical bursts in a row would sound like a loop
    src.start(t, Math.random() * 1.5); src.stop(t + dur + 0.05); track(src);
  };
  const arp = (notes: number[], step: number, dur: number, o: ToneOpts) => notes.forEach((f, i) => tone(f, i * step, dur, o));

  const A: Record<string, () => void> = {
    // countdown: soft bell, then a bright chord for the start
    beep: () => { tone(660, 0, 0.32, { vol: 0.09 }); tone(1320, 0, 0.18, { type: 'sine', vol: 0.03 }); },
    go: () => { for (const f of [880, 1109, 1319]) tone(f, 0, 0.7, { vol: 0.06, detune: 6 }); tone(1760, 0, 0.35, { type: 'sine', vol: 0.03 }); },
    box: () => arp([784, 988, 1175, 1568], 0.045, 0.12, { vol: 0.05, verb: 1.4 }),
    item: () => { tone(988, 0, 0.12, { vol: 0.06, detune: 5 }); tone(1319, 0.08, 0.32, { vol: 0.07, detune: 5 }); tone(2637, 0.08, 0.2, { type: 'sine', vol: 0.015 }); },
    use: () => { tone(620, 0, 0.16, { vol: 0.07, to: 300 }); noise(0, 0.18, { filter: 'bandpass', f: 2500, to: 700, q: 1.5, vol: 0.05 }); },
    drop: () => { tone(260, 0, 0.16, { type: 'sine', vol: 0.12, to: 90 }); noise(0, 0.06, { f: 900, vol: 0.05 }); },
    fire: () => { tone(160, 0, 0.3, { type: 'sawtooth', vol: 0.06, to: 720, lp: 1800 }); noise(0, 0.28, { filter: 'bandpass', f: 700, to: 4000, q: 1.2, vol: 0.07 }); },
    boost: () => {
      noise(0, 0.7, { filter: 'bandpass', f: 400, to: 3200, q: 1.1, vol: 0.09, attack: 0.05 });
      tone(110, 0, 0.6, { type: 'sawtooth', vol: 0.05, to: 440, lp: 1400, detune: 10 });
      tone(55, 0, 0.5, { type: 'sine', vol: 0.08, to: 110 });
    },
    charge: () => arp([330, 392, 494, 659], 0.07, 0.14, { vol: 0.05, detune: 8, lp: 3000 }),
    hurt: () => {
      tone(420, 0, 0.45, { type: 'sawtooth', vol: 0.07, to: 70, lp: 1300 });
      tone(130, 0, 0.25, { type: 'sine', vol: 0.14, to: 40, verb: 0.3 });
      noise(0, 0.32, { f: 1600, to: 300, vol: 0.12 });
    },
    hit: () => { noise(0, 0.14, { filter: 'bandpass', f: 1400, to: 500, q: 1, vol: 0.06 }); tone(150, 0, 0.1, { type: 'sine', vol: 0.05, to: 60 }); },
    shield: () => { tone(1200, 0, 0.4, { type: 'sine', vol: 0.06, to: 600, detune: 12 }); tone(2400, 0, 0.25, { type: 'sine', vol: 0.02, to: 1800, verb: 1.6 }); },
    warn: () => { tone(1000, 0, 0.1, { type: 'square', vol: 0.045, lp: 2600 }); tone(1000, 0.15, 0.1, { type: 'square', vol: 0.045, lp: 2600 }); },
    storm: () => { noise(0, 1.3, { f: 700, to: 160, vol: 0.13, attack: 0.08, verb: 1 }); tone(55, 0.05, 1, { type: 'sine', vol: 0.09, to: 38 }); },
    lap: () => { tone(660, 0, 0.16, { vol: 0.08, detune: 4 }); tone(880, 0.12, 0.3, { vol: 0.08, detune: 4 }); tone(1760, 0.12, 0.2, { type: 'sine', vol: 0.02 }); },
    finish: () => {
      arp([523, 659, 784, 1047], 0.11, 0.22, { vol: 0.06, detune: 6 });
      for (const f of [784, 1047, 1319]) tone(f, 0.48, 0.9, { vol: 0.05, detune: 7, verb: 1.4 });
    },
    lose: () => arp([392, 330, 262], 0.2, 0.36, { vol: 0.07, lp: 1500 }),
    elim: () => tone(300, 0, 0.28, { vol: 0.06, to: 150, lp: 1500 }),
    deny: () => { tone(150, 0, 0.08, { type: 'square', vol: 0.05, lp: 900 }); tone(140, 0.1, 0.1, { type: 'square', vol: 0.05, lp: 900 }); },
    interval: () => { tone(784, 0, 0.18, { type: 'sine', vol: 0.09 }); tone(1175, 0.14, 0.4, { type: 'sine', vol: 0.09 }); tone(2350, 0.14, 0.25, { type: 'sine', vol: 0.02 }); },
    tick: () => tone(1500, 0, 0.05, { type: 'sine', vol: 0.045, verb: 0.3 }),
    levelup: () => {
      arp([523, 659, 784, 1047, 1319, 1568], 0.08, 0.3, { vol: 0.055, detune: 6, verb: 1.4 });
      noise(0.45, 0.5, { filter: 'highpass', f: 6000, vol: 0.025, attack: 0.05, verb: 1.5 });
    },
    // FTP up on the end screen: a climb in fourths and fifths, a bright bell on top (lighter than the level-up)
    ftpup: () => {
      arp([392, 523, 659, 784, 1047], 0.07, 0.26, { vol: 0.05, detune: 5, verb: 1.3 });
      tone(1568, 0.38, 0.6, { type: 'sine', vol: 0.03, verb: 1.6 });
      noise(0.35, 0.4, { filter: 'highpass', f: 7000, vol: 0.02, attack: 0.04, verb: 1.5 });
    },
    click: () => tone(1200, 0, 0.03, { type: 'sine', vol: 0.03, verb: 0 }),
    // a supporter's cheer: a swell of crowd noise and two bright notes; with the crowd boost, a third one on top
    cheer: () => { noise(0, 0.55, { filter: 'bandpass', f: 700, to: 1900, q: 0.7, vol: 0.07, attack: 0.04, verb: 1.5 }); tone(880, 0.04, 0.22, { vol: 0.035, detune: 9 }); tone(1175, 0.16, 0.3, { vol: 0.035, detune: 9 }); },
    cheerBoost: () => {
      noise(0, 0.7, { filter: 'bandpass', f: 700, to: 2400, q: 0.7, vol: 0.08, attack: 0.04, verb: 1.5 });
      arp([880, 1175, 1568], 0.1, 0.32, { vol: 0.04, detune: 9 }); tone(2349, 0.3, 0.35, { type: 'sine', vol: 0.02, verb: 1.6 });
    },
    // derailleur: a sharp metallic click, then the chain settling on the next sprocket
    shift: () => { noise(0, 0.025, { filter: 'bandpass', f: 3800, q: 3, vol: 0.09, verb: 0.2 }); noise(0.045, 0.04, { filter: 'bandpass', f: 2400, q: 2, vol: 0.06, verb: 0.2 }); },
  };
  A[name]?.();
}
